// =============================================================================
// Resource Exceptions Panel - Assenze / Presenze aggiuntive (singola risorsa)
// =============================================================================
// Gestisce le eccezioni alla disponibilità standard di una risorsa:
// - Assenze (ferie, malattia, manutenzione...) -> isAvailable=false
// - Presenze aggiuntive (aperture straordinarie) -> isAvailable=true
// Sia per giorni singoli che per periodi, a giornata intera o a fascia oraria.

import { useState } from 'react';
import { Plus, Loader2, CalendarOff } from 'lucide-react';
import { EmptyState } from '../common';
import { useTranslation } from 'react-i18next';
import {
  useResourceExceptions,
  useDeleteResourceException,
  ResourceException,
} from '../../api/hooks';
import { ExceptionForm } from './ExceptionForm';
import { ExceptionRow } from './ExceptionRow';

interface ResourceExceptionsPanelProps {
  resourceId: string;
}

export function ResourceExceptionsPanel({ resourceId }: ResourceExceptionsPanelProps) {
  const { t } = useTranslation('settings');
  const { data: exceptions, isLoading } = useResourceExceptions(resourceId);
  const deleteException = useDeleteResourceException();

  const [editing, setEditing] = useState<ResourceException | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleDelete = async (exc: ResourceException) => {
    if (!window.confirm(t('exceptions.deleteConfirm', { title: exc.title }))) return;
    try {
      await deleteException.mutateAsync({ resourceId, exceptionId: exc.id });
    } catch (error) {
      console.error('Error deleting exception:', error);
    }
  };

  const closeForm = () => {
    setEditing(null);
    setIsCreating(false);
  };

  if (isCreating || editing) {
    return <ExceptionForm resourceId={resourceId} exception={editing} onClose={closeForm} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{t('exceptions.subtitle')}</p>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('exceptions.add')}
        </button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center">
          <Loader2 className="w-6 h-6 text-emerald-600 animate-spin mx-auto" />
        </div>
      ) : !exceptions || exceptions.length === 0 ? (
        <EmptyState icon={CalendarOff} title={t('exceptions.empty')} size="sm" className="py-12" />
      ) : (
        <ul className="space-y-2">
          {exceptions.map((exc) => (
            <ExceptionRow
              key={exc.id}
              exception={exc}
              onEdit={() => setEditing(exc)}
              onDelete={() => handleDelete(exc)}
              deleting={deleteException.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
